/* eslint-disable spire/export-styles */
import useEmblaCarousel from "embla-carousel-react";
import React, { FC, useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Img from "@insite/mobius/Img";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY } from "@tCentro/theme/design";
import * as cssLinter from "css";
import parse from "html-react-parser";

const enum fields {
    slideIndicator = "slideIndicator",
    modalHeading = "modalHeading",
    backgroundImage = "backgroundImage",
    slides = "slides",
    slideTitle = "slideTitle",
    slideheading = "slideheading",
    slidesubheading = "slidesubheading",
    slidegifImage = "slidegifImage",
    customCSS = "customCSS",
    slidebuttonLabel = "slidebuttonLabel",
    slidebuttonLabelTwo = "buttonLabelTwo",
    autoplay = "autoplay",
    showModal = "showModal",
    delayPopUp = "delayPopUp",
    delaySeconds = "delaySeconds",
}

interface SlideModal {
    fields: {
        slideTitle: string;
        slideheading: string;
        slidesubheading: string;
        slideImg: string;
        slidebuttonLabel: string;
        slidebuttonLabelTwo: string;
        customCSS: string;
        slidegifImage: string;
    };
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.slideIndicator]: boolean;
        [fields.showModal]: boolean;
        [fields.autoplay]: number;
        [fields.modalHeading]: string;
        [fields.backgroundImage]: string;
        [fields.slides]: SlideModal[];
        [fields.customCSS]: string;
        [fields.delayPopUp]: boolean;
        [fields.delaySeconds]: number;
    };
    extendedStyles?: SlideshowStyles;
}

type Props = OwnProps;

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    mainContentStyles?: InjectableCss;
    buttonContainerStyles?: InjectableCss;
    imgStyles?: InjectableCss;
    imgContainer?: InjectableCss;
    parentContentContainer?: InjectableCss;
    skipButtonStyles?: InjectableCss;

    // BackgroundImage and Modal Styles
    contentContainer?: InjectableCss;
    childContainer?: InjectableCss;
    imageStylesContainer?: InjectableCss;
    imageStyles?: InjectableCss;
    modalStyles?: ModalPresentationProps;
    imageGifStyles?: InjectableCss;
    mainTitleStyles?: InjectableCss;
    whitebuttonStyles?: InjectableCss;
    headingStyles?: InjectableCss;
    mainParentContentContainer?: InjectableCss;
    greenButtonStyles?: InjectableCss;
    shopButtonStyles?: InjectableCss;
    nextButtonStyles?: InjectableCss;
}
export const slideshowStyles: SlideshowStyles = {
    skipButtonStyles: {
        css: css`
            background: none !important;
            border-radius: 40px;
            color: ${getColor("secondary")};
            > span {
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                text-align: center;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 45%;
                        > span {
                            font-size: ${TYPOGRAPHY.size.smaller};
                            font-weight: ${TYPOGRAPHY.weight.mediumbold};
                            line-height: 16px;
                        }
                    `,
                    css`
                        > span {
                            font-size: ${TYPOGRAPHY.size.smaller};
                            font-weight: ${TYPOGRAPHY.weight.mediumbold};
                            line-height: 16px;
                        }
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    nextButtonStyles: {
        css: css`
            margin-left: 16px;
            border-radius: 40px;
            > span {
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                text-align: center;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 45%;
                        > span {
                            font-size: ${TYPOGRAPHY.size.smaller};
                            font-weight: ${TYPOGRAPHY.weight.mediumbold};
                            line-height: 16px;
                        }
                    `,
                    css`
                        > span {
                            font-size: ${TYPOGRAPHY.size.smaller};
                            font-weight: ${TYPOGRAPHY.weight.mediumbold};
                            line-height: 16px;
                        }
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    shopButtonStyles: {
        css: css`
            background-color: #8f0446;
            color: #ffffff;
            border-radius: 40px;
            > span {
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                text-align: center;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        > span {
                            font-size: ${TYPOGRAPHY.size.smaller};
                            font-weight: ${TYPOGRAPHY.weight.mediumbold};
                            line-height: 16px;
                        }
                    `,
                    css`
                        > span {
                            font-size: ${TYPOGRAPHY.size.smaller};
                            font-weight: ${TYPOGRAPHY.weight.mediumbold};
                            line-height: 16px;
                        }
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    buttonContainerStyles: {
        css: css`
            margin: 20px 0px 24px 0px;
            display: flex;
            justify-content: center;
            width: 100%;
        `,
    },
    parentContentContainer: {
        css: css`
            padding: 40px 40px 0px 40px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 20px 16px 0px 16px;
                    `,
                    css`
                        padding: 20px 16px 0px 16px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    imgStyles: {
        css: css`
            width: 100%;
            object-fit: contain;
            height: 100%;
        `,
    },
    imgContainer: {
        css: css`
            max-width: 309px;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 174px;
                    `,
                    css`
                        max-width: 174px;
                    `,
                    css`
                        max-width: 152px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    contentContainer: {
        css: css`
            display: flex;
            width: 100%;
            align-items: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        align-items: center;
                    `,
                    css`
                        flex-direction: column;
                        align-items: center;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    childContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-left: 32px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 0px;
                        margin-top: 32px;
                        align-items: center;
                    `,
                    css`
                        margin-left: 0px;
                        margin-top: 32px;
                        align-items: center;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    mainTitleStyles: {
        css: css`
            font-size: 32px;
            font-weight: 700;
            line-height: 40px;
            color: ${getProp("theme.typography.h1.color")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-weight: 600;
                        font-size: 24px;
                        line-height: 32px;
                        text-align: center;
                    `,
                    css`
                        font-weight: 600;
                        font-size: 24px;
                        line-height: 32px;
                    `,
                    css`
                        font-weight: 600;
                        font-size: 24px;
                        line-height: 32px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    mainContentStyles: {
        css: css`
            p {
                font-size: 14px;
                font-weight: 400;
                line-height: 24px;
                color: ${getProp("theme.typography.p.color")};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            font-weight: 400;
                            font-size: 14px;
                            line-height: 24px;
                        `,
                        css`
                            font-weight: 400;
                            font-size: 14px;
                            line-height: 24px;
                        `,
                        css`
                            font-weight: 400;
                            font-size: 14px;
                            line-height: 24px;
                        `,
                        null,
                        null,
                    ])}
            }
            margin-top: 24px;
        `,
    },
    slideshowWrapper: {
        css: css`
            position: relative;
        `,
    },

    prevArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: -webkit-box;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: white;
            background-repeat: no-repeat;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
        `,
    },
    dotButton: {
        css: css`
            height: 3px;
            width: 30px;
            padding: 0;
            margin: 0 4px;
            border: 0;
            background: ${getColor("common.border")};
            &:hover,
            &:active {
                background: ${getColor("common.border")};
            }
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
            right: 0;
        `,
    },
    nextArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },

    // -------------Background-----------------

    whitebuttonStyles: {
        css: css`
            border-radius: 50%;
            width: 16px;
            height: 16px;
            background: #e0dfe0;
        `,
    },
    greenButtonStyles: {
        css: css`
            border-radius: 50%;
            width: 16px;
            height: 16px;
            background: ${getColor("primary")};
            margin: 0px 16px;
        `,
    },
    mainParentContentContainer: {
        css: css`
            position: relative;
            bottom: 20px;
            background-color: white;
            z-index: 4;
        `,
    },
    headingStyles: {
        css: css`
            font-family: "Poppins";
            font-size: 56px;
            font-weight: 800;
            width: 80%;
            line-height: 64px;
            color: white;
            position: absolute;
            top: 80px;
            left: 104px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 32px;
                        line-height: 40px;
                        left: 32px;
                        right: 32px;
                    `,
                    css`
                        font-size: 32px;
                        line-height: 40px;
                    `,
                    css`
                        font-weight: 800;
                        font-size: 40px;
                        line-height: 48px;
                        top: 80px;
                        left: 92px;
                    `,
                    null,
                    null,
                ])}
        `,
    },

    modalStyles: {
        cssOverrides: {
            modalBody: css`
                max-width: 936px !important;
                width: 936px;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 100vw;
                        `,
                        css`
                            width: 100vw;
                        `,
                        css`
                            width: 610px !important;
                            height: auto;
                        `,
                        null,
                        null,
                    ])}
            `,
            modalContent: css`
                padding: 0px;
            `,
            modalTitle: css`
                padding: 0px;
                border: none;
            `,
        },

        closeButtonProps: {
            css: css`
                background: none;
                position: absolute;
                top: 25px;
                right: 0px;
                z-index: 4;
                color: #ffffff !important;
                border: none;
                > span {
                    color: #ffffff !important;
                }
                &:focus {
                    border: none;
                    background: none;
                    outline-color: transparent;
                }
            `,
            hoverMode: "none",
        },
        closeButtonIconProps: {
            size: 24,
        },
    },

    imageStylesContainer: {
        css: css`
            height: 288px;
            background: linear-gradient(90deg, #8f0446 0%, #1d2d36 100%);
            position: relative;
        `,
    },
    imageStyles: {
        css: css`
            width: 100%;
            height: 100%;
            object-fit: cover;
            opacity: 0.2;
        `,
    },
};

const defaultCustomCss = `.slideshow-wrapper {
}

.slideshow-carousel {
}



.slide-wrapper {
}

.slide-heading {
}

.slide-subheading {
}

.slide-button {
}

.slide-indicator {
}

.slide-dot-button {
}


`;
const CENHomePageGraphicModal: FC<Props> = ({ fields, extendedStyles }) => {
    const { isInShell } = useShellContext();
    const history = useHistory();

    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);

    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [isMobile, setIsMobile] = React.useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    useEffect(() => {
        const modalShown = localStorage.getItem("modalShown");
        const useragent = navigator.userAgent;

        if (modalShown || fields.showModal === false || useragent.includes("Googlebot")) {
            return;
        }

        if (!modalShown) {
            if (fields.delayPopUp) {
                const delaySeconds = fields.delaySeconds * 1000;
                const timerId = setTimeout(() => {
                    setModalIsOpen(true);
                    localStorage.setItem("modalShown", "true");
                }, delaySeconds);

                return () => clearTimeout(timerId);
            }
            setModalIsOpen(true);
            // Store a value in local storage to show that the modal has been shown
            localStorage.setItem("modalShown", "true");
        }
    }, []);

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, fields.slides]);

    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    // useEffect(() => {
    //     if (typeof window !== undefined) {
    //         const handleResize = () => {
    //             const isMobile = window.matchMedia("(max-width: 992px)").matches;
    //             setIsMobile(isMobile);
    //         };

    //         handleResize(); // Initial check

    //         window.addEventListener("resize", handleResize);

    //         return () => {
    //             window.removeEventListener("resize", handleResize);
    //         };
    //     }
    // }, []);

    return (
        <Modal isOpen={modalIsOpen} handleClose={modalCloseHandler} {...styles.modalStyles}>
            <StyledWrapper {...styles.imageStylesContainer}>
                <Img src={fields.backgroundImage} altText={fields.modalHeading} {...styles.imageStyles} />
            </StyledWrapper>
            <Typography as="p" {...styles.headingStyles}>
                {fields.modalHeading}
            </Typography>
            <StyledWrapper {...styles.mainParentContentContainer}>
                <StyledWrapper {...customCssWrapper}>
                    <StyledWrapper className="slideshow-wrapper" {...styles.slideshowWrapper}>
                        <StyledWrapper className="slideshow-carousel" {...styles.carouselContainer} ref={emblaRef}>
                            <StyledWrapper {...styles.slideContainerWrapper}>
                                {fields.slides.map((slide, index) => {
                                    // const buttonLink = buttonLinks[index];
                                    // const buttonLink1=buttonLinks1[index];
                                    return (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <StyledWrapper
                                            className="slide-wrapper"
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={index}
                                            {...styles.parentContentContainer}
                                        >
                                            <StyledWrapper {...styles.contentContainer}>
                                                <StyledWrapper {...styles.imgContainer}>
                                                    <Img src={slide.fields.slidegifImage} altText={slide.fields.slideheading} {...styles.imgStyles} />
                                                </StyledWrapper>
                                                <StyledWrapper {...styles.childContainer}>
                                                    <Typography as="h1" {...styles.mainTitleStyles}>
                                                        {slide.fields.slideheading}
                                                    </Typography>
                                                    <Typography as="p" {...styles.mainContentStyles}>
                                                        {parse(
                                                            transformContentHtml(slide.fields.slidesubheading),
                                                            parserOptions,
                                                        )}
                                                    </Typography>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.buttonContainerStyles}>
                                                <Button
                                                    {...(index === 2
                                                        ? { ...styles.shopButtonStyles }
                                                        : { ...styles.skipButtonStyles })}
                                                    onClick={modalCloseHandler}
                                                >
                                                    {slide.fields.slidebuttonLabel}
                                                </Button>
                                                {index !== 2 && (
                                                    <Button
                                                        onClick={() => embla && embla.scrollNext()}
                                                        {...styles.nextButtonStyles}
                                                    >
                                                        {slide.fields.slidebuttonLabelTwo}
                                                    </Button>
                                                )}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    );
                                })}
                            </StyledWrapper>
                        </StyledWrapper>
                        {fields.slideIndicator && (
                            <StyledWrapper className="slide-indicator" {...styles.dotsContainerWrapper}>
                                {fields.slides.map((_, i) => {
                                    const dotColorStyles =
                                        i === selectedIndex
                                            ? `
                                        background: ${getColor("primary")};
                                        border-radius:50%;
                                        height:15.56px;
                                        min-height:unset;
                                        width:16px;
                                        &:hover { background: ${getColor("primary")}; }
                                    `
                                            : `background: #E0DFE0;
                                    border-radius:50%;
                                    height:16px;
                                    min-height:unset;
                                    width:16px;
                                    &:hover { background:#E0DFE0;}`;
                                    const dotbuttonContainerStyles = {
                                        css: css`
                                            ${styles.dotButton?.css || ""}
                                            ${dotColorStyles}
                                        `,
                                    };

                                    return (
                                        <Button
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={i}
                                            {...dotbuttonContainerStyles}
                                            onClick={() => embla && embla.scrollTo(i)}
                                        />
                                    );
                                })}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </Modal>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const HomePageGraphicModal: WidgetModule = {
    component: CENHomePageGraphicModal,
    definition: {
        group: "Common",
        displayName: "CEN Graphic Modal",
        fieldDefinitions: [
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                fieldType: "Translatable",
                name: fields.modalHeading,
                editorTemplate: "TextField",
                displayName: "Heading",
                defaultValue: "",
            },
            {
                name: fields.showModal,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.backgroundImage,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Background Image",
            },
            {
                name: fields.delayPopUp,
                displayName: "Delay Popup",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.delaySeconds,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                defaultValue: 7,
                tab: settingsTab,
                isVisible: item => item?.fields[fields.delayPopUp],
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },

                    {
                        name: "slideheading",
                        editorTemplate: "TextField",
                        displayName: "Slide Heading",
                        defaultValue: "",
                    },
                    {
                        name: "slidesubheading",
                        editorTemplate: "RichTextField",
                        displayName: "Slide Subheading",
                        defaultValue: "",
                        // extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: fields.slidegifImage,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        displayName: "Gif",
                    },

                    {
                        name: "slidebuttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label ",
                        defaultValue: "",
                    },

                    {
                        name: "slidebuttonLabelTwo",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default HomePageGraphicModal;
